/**
 * assets/free-gift-sync.js
 * ------------------------------------------------------------------
 * FREE GIFTS WITH PURCHASE — Paws Fountain
 * ------------------------------------------------------------------
 * Five independent client-side rules (no app, no Shopify Plus /
 * checkout customization — Basic plan, storefront cart only), all
 * enforced by the same reconciliation engine below. Each rule runs in
 * one of two modes:
 *
 *   'exact' — the gift line's quantity is forced to match desiredQty
 *             exactly, in both directions (manually adding extra
 *             gets clamped back down). Used for placeholder $0
 *             products that only exist to be free — there's no
 *             legitimate reason a customer would want "extra" of
 *             them.
 *
 *   'floor' — the gift line's quantity is only ever topped UP to
 *             desiredQty if it's short; it is never clamped down.
 *             Used for rules 4-5 below, where the "gift" is a real,
 *             independently-purchasable product — a customer may
 *             legitimately want more than their free quota, and any
 *             units beyond the free quota simply ring up at full
 *             price (enforced by the Buy-X-Get-Y automatic discounts
 *             described below, not by this script).
 *
 * THE FIVE RULES
 * ------------------------------------------------------------------
 *   1. [exact] For every unit of the 4L Fountain (variant
 *      48032713212078) in the cart, the cart must contain that many
 *      units of Free Starter Filter - 4L (variant 48113965826222,
 *      $0.00 placeholder product).
 *
 *   2. [exact] Same as 1, for the 7L Fountain (variant
 *      48032713244846) → Free Starter Filter - 7L (variant
 *      48115561824430, $0.00 placeholder product).
 *
 *   3. [exact] If the cart contains at least one unit of anything
 *      OTHER than the gift items themselves (rules 1, 2, 4, 5 — see
 *      GIFT_VARIANT_IDS), the cart must contain exactly 1 unit of
 *      1 Year Warranty Guarantee (variant 48115565461678, $0.00) —
 *      added to every order storewide, capped at 1 regardless of
 *      order size. Rules 4-5's variant ids are intentionally NOT in
 *      GIFT_VARIANT_IDS: those are real products, so a standalone
 *      purchase of one (no fountain in cart) should still count as a
 *      "real" order and get a warranty.
 *
 *   4. [floor] For every unit of the 4L Fountain in the cart, the
 *      cart must contain AT LEAST that many units of "The Paws
 *      Filters - 4L: 3 Filters (6 Weeks)" (variant 48032810172590,
 *      the cheapest of that product's three variants, normally
 *      $20.00). This is a real, independently-purchasable product —
 *      not a $0 placeholder — so it's made free via an automatic
 *      Buy-X-Get-Y discount in Shopify admin ("Free 3-Filter Refill
 *      (6 Weeks) — 4L Fountain": buy 1x 4L Fountain, get 1x this
 *      variant at 100% off, up to 50x per order), NOT by this script.
 *      This script's only job is to make sure at least the free
 *      quota is physically in the cart; the discount engine decides
 *      which units (up to the fountain-matched quantity) are free —
 *      any customer-added units beyond that ring up at full price
 *      automatically, with no clamping needed here.
 *
 *   5. [floor] Same as 4, for the 7L Fountain → "The Paws Filters -
 *      7L: 3 Filters (6 Weeks)" (variant 48103284310190), covered by
 *      the "Free 3-Filter Refill (6 Weeks) — 7L Fountain" automatic
 *      discount.
 *
 * In all cases: removing the triggering fountain(s) stops rules 1-2's
 * placeholder gifts from being replenished (and existing units get
 * clamped to 0 next reconcile pass, since they're 'exact'). Rules 4-5
 * simply stop being topped up — any units already in the cart stay
 * there, they just revert to full price once the Buy-X-Get-Y
 * discount's prerequisite (the fountain) is no longer met, which
 * Shopify's discount engine handles automatically at checkout.
 *
 * WHERE THIS HOOKS IN
 * ------------------------------------------------------------------
 * Three independent detection layers, since no single one reliably
 * catches every way the cart can change on this theme:
 *
 *  1. The theme's own cart-update PubSub event (assets/constants.js /
 *     assets/pubsub.js / assets/cart.js) — fires after quantity-
 *     stepper changes on the cart page and cart drawer.
 *
 *  2. A window.fetch wrapper watching for POSTs to /cart/add.js,
 *     /cart/change.js, /cart/update.js, /cart/clear.js. Needed
 *     because sections/pf-pdp-hero.liquid's product-page "Add to
 *     cart" button (and its "Add the Paws Mat" cross-sell) call
 *     fetch('/cart/add.js', ...) directly and then re-render the
 *     drawer via cart-drawer.js's own renderContents() — neither of
 *     which publishes cart-update — and each free gift product has
 *     its own live, independently-browsable product page that
 *     customers can add from directly. None of that goes through
 *     hook 1.
 *
 *  3. A MutationObserver on document.body watching for childList
 *     changes. Needed because snippets/cart-boost.liquid's own
 *     upsell "+ Add" button refreshes the cart drawer by directly
 *     overwriting #CartDrawer's innerHTML — bypassing both the
 *     PubSub event AND happening after a fetch call hook 2 already
 *     handled, so without this the quantity-input lock (see below)
 *     wouldn't get re-applied to the freshly-rendered drawer markup.
 *     This is also the catch-all for any other/future code path that
 *     changes the cart or re-renders cart HTML in a way hooks 1-2
 *     don't anticipate.
 *
 * We also run once on DOMContentLoaded on every page, since the cart
 * drawer can be opened from any page, including ones where no
 * add/change happened this session (e.g. a fresh reload after the
 * cart was edited in another tab).
 *
 * DEBOUNCING
 * ------------------------------------------------------------------
 * Leading + trailing edge: the first trigger in an idle period runs
 * immediately (so a single add/change gets corrected with no visible
 * delay), and if more triggers arrive while that run is still
 * "cooling down", exactly one trailing run fires after the cooldown
 * to capture the final settled state.
 *
 * RECONCILIATION (not just a single read-then-write)
 * ------------------------------------------------------------------
 * A plain "read /cart.js once, write once" pass is fragile: if two
 * fountains are added in quick succession, or a write is silently
 * rejected, a single pass can under- or over-correct and never get
 * revisited. Instead, every sync pass RECONCILES: read the cart,
 * compute the desired quantity for EACH rule above, write corrections
 * for every rule that's wrong (per its mode — see above), then
 * re-read to CONFIRM the writes actually landed — up to
 * MAX_RECONCILE_ATTEMPTS times.
 *
 * IMPORTANT API GOTCHA (confirmed against the live Ajax API):
 * /cart/change.js's `id` param must be a STRING. Sending it as a JSON
 * number gets silently rejected with a 400 ("expected Integer to be
 * a ... digit-string pattern"). /cart/add.js tolerates a numeric id,
 * but we send a string to both for consistency rather than relying
 * on that leniency.
 *
 * LOOP SAFETY
 * ------------------------------------------------------------------
 * Our own /cart/add.js and /cart/change.js calls are tagged with a
 * custom header (X-Free-Gift-Sync: 1). The fetch wrapper ignores
 * requests carrying that header when deciding whether to schedule a
 * follow-up sync, so the reconciliation loop's own writes don't
 * re-trigger hook 2. The MutationObserver only watches childList (not
 * attributes), so it doesn't react to our own DOM lock changes either.
 *
 * QUANTITY LOCK
 * ------------------------------------------------------------------
 * Each 'exact'-mode gift item's own quantity stepper (in the cart
 * drawer and on /cart) is disabled — its quantity is fully controlled
 * by this script, not by the customer. 'floor'-mode rules (4-5) are
 * deliberately left unlocked, since customers may legitimately want
 * to buy more than their free quota. Because the theme (and
 * cart-boost) re-render the cart's HTML from the server on every
 * update, wiping any DOM changes we made, we re-apply the lock on
 * every detection-layer trigger, not just once on load.
 */
(function () {
  'use strict';

  var FOUR_L_FOUNTAIN_VARIANT_ID = 48032713212078;
  var SEVEN_L_FOUNTAIN_VARIANT_ID = 48032713244846;
  var FOUR_L_FILTER_VARIANT_ID = 48113965826222; // Free Starter Filter - 4L, $0.00
  var SEVEN_L_FILTER_VARIANT_ID = 48115561824430; // Free Starter Filter - 7L, $0.00
  var WARRANTY_VARIANT_ID = 48115565461678; // 1 Year Warranty Guarantee, $0.00
  var FOUR_L_REFILL_VARIANT_ID = 48032810172590; // The Paws Filters - 4L, "3 Filters (6 Weeks)", $20 — made free via automatic Buy-X-Get-Y discount when a 4L Fountain is in cart
  var SEVEN_L_REFILL_VARIANT_ID = 48103284310190; // The Paws Filters - 7L, "3 Filters (6 Weeks)", $20 — made free via automatic Buy-X-Get-Y discount when a 7L Fountain is in cart

  // Placeholder $0 gift products only — excluded from the warranty
  // rule's "did the customer buy anything real" check. The two
  // REFILL variant ids above are intentionally NOT included here:
  // they're real, independently-purchasable products, so a
  // standalone purchase of one (no fountain in cart) should still
  // count toward "every order gets a warranty".
  var GIFT_VARIANT_IDS = [FOUR_L_FILTER_VARIANT_ID, SEVEN_L_FILTER_VARIANT_ID, WARRANTY_VARIANT_ID];

  var GIFT_RULES = [
    {
      id: FOUR_L_FILTER_VARIANT_ID,
      mode: 'exact',
      label: 'Free Starter Filter - 4L',
      desiredQty: function (cart) {
        return sumQtyFor(cart, [FOUR_L_FOUNTAIN_VARIANT_ID]);
      },
    },
    {
      id: SEVEN_L_FILTER_VARIANT_ID,
      mode: 'exact',
      label: 'Free Starter Filter - 7L',
      desiredQty: function (cart) {
        return sumQtyFor(cart, [SEVEN_L_FOUNTAIN_VARIANT_ID]);
      },
    },
    {
      id: WARRANTY_VARIANT_ID,
      mode: 'exact',
      label: '1 Year Warranty Guarantee',
      desiredQty: function (cart) {
        var realQty = cart.items.reduce(function (sum, item) {
          return GIFT_VARIANT_IDS.indexOf(item.variant_id) === -1 ? sum + item.quantity : sum;
        }, 0);
        return realQty > 0 ? 1 : 0;
      },
    },
    {
      id: FOUR_L_REFILL_VARIANT_ID,
      mode: 'floor',
      label: '3 Filters (6 Weeks) - 4L (free with 4L Fountain)',
      desiredQty: function (cart) {
        return sumQtyFor(cart, [FOUR_L_FOUNTAIN_VARIANT_ID]);
      },
    },
    {
      id: SEVEN_L_REFILL_VARIANT_ID,
      mode: 'floor',
      label: '3 Filters (6 Weeks) - 7L (free with 7L Fountain)',
      desiredQty: function (cart) {
        return sumQtyFor(cart, [SEVEN_L_FOUNTAIN_VARIANT_ID]);
      },
    },
  ];

  function sumQtyFor(cart, variantIds) {
    return cart.items.reduce(function (sum, item) {
      return variantIds.indexOf(item.variant_id) !== -1 ? sum + item.quantity : sum;
    }, 0);
  }

  var SYNC_HEADER = 'X-Free-Gift-Sync';
  var DEBOUNCE_MS = 400;
  var MAX_RECONCILE_ATTEMPTS = 4;
  var RECONCILE_RETRY_DELAY_MS = 300;

  var cooldownTimer = null;
  var trailingNeeded = false;
  var syncInFlight = false;
  var rerunPending = false;

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  // Leading + trailing debounce: fires immediately on the first call
  // of a burst, then coalesces any further calls into one trailing
  // call after DEBOUNCE_MS of quiet.
  function scheduleSync() {
    if (cooldownTimer) {
      trailingNeeded = true;
      return;
    }
    runSync();
    cooldownTimer = setTimeout(function () {
      cooldownTimer = null;
      if (trailingNeeded) {
        trailingNeeded = false;
        scheduleSync();
      }
    }, DEBOUNCE_MS);
  }

  function runSync() {
    if (syncInFlight) {
      // A sync is already mid-flight — don't overlap requests, just
      // remember to run once more right after this one finishes,
      // since the cart may have changed again in the meantime.
      rerunPending = true;
      return;
    }
    syncInFlight = true;

    reconcile(1)
      .catch(function (err) {
        console.error('[free-gift-sync]', err);
      })
      .finally(function () {
        syncInFlight = false;
        if (rerunPending) {
          rerunPending = false;
          scheduleSync();
        }
      });
  }

  // Reads the live cart, computes what EACH gift rule's quantity
  // *should* be, writes corrections for every rule that's wrong (per
  // its mode — 'exact' rules are forced to match exactly in both
  // directions; 'floor' rules are only ever topped up, never clamped
  // down), then recurses to CONFIRM the writes actually took (rather
  // than trusting them blindly) — up to MAX_RECONCILE_ATTEMPTS passes.
  function reconcile(attempt) {
    return getCart().then(function (cart) {
      var mismatches = [];

      GIFT_RULES.forEach(function (rule) {
        // Sum across ALL lines matching this variant, not just the first/last
        // one found — if more than one line of the same variant ever exists
        // (e.g. from a race between this script and a customer/upsell click
        // both adding the same item around the same time), under-counting
        // from a single-line lookup would make the reconciler think there's
        // still a shortfall when there isn't, adding yet another line on top.
        var line = null;
        cart.items.forEach(function (item) {
          if (item.variant_id === rule.id && !line) line = item;
        });
        var currentQty = sumQtyFor(cart, [rule.id]);
        var desiredQty = rule.desiredQty(cart);

        if (rule.mode === 'floor') {
          if (currentQty < desiredQty) {
            mismatches.push({
              rule: rule,
              line: line,
              currentQty: currentQty,
              desiredQty: desiredQty,
              shortfall: desiredQty - currentQty,
            });
          }
        } else if (currentQty !== desiredQty) {
          mismatches.push({ rule: rule, line: line, currentQty: currentQty, desiredQty: desiredQty });
        }
      });

      if (mismatches.length === 0) {
        return; // converged — nothing to do
      }

      if (attempt > MAX_RECONCILE_ATTEMPTS) {
        console.warn(
          '[free-gift-sync] gave up after ' + (attempt - 1) + ' attempts — still mismatched: ' +
          mismatches
            .map(function (m) {
              return m.rule.label + ' (have ' + m.currentQty + ', want ' + m.desiredQty + ')';
            })
            .join('; ')
        );
        return;
      }

      var writes = mismatches.map(function (m) {
        if (m.rule.mode === 'floor') {
          // Only ever top up by the shortfall — /cart/add.js
          // increments an existing line or creates a new one, either
          // way landing exactly on desiredQty from currentQty.
          return writeCart('/cart/add.js', { id: String(m.rule.id), quantity: m.shortfall });
        }
        // Line doesn't exist yet: /cart/change.js can't create a new
        // line, so use /cart/add.js. Otherwise (line exists, or we're
        // zeroing it out) use /cart/change.js, which sets an absolute
        // quantity rather than incrementing.
        var url = !m.line && m.desiredQty > 0 ? '/cart/add.js' : '/cart/change.js';
        return writeCart(url, { id: String(m.rule.id), quantity: m.desiredQty });
      });

      return Promise.all(writes)
        .then(function () {
          return delay(RECONCILE_RETRY_DELAY_MS);
        })
        .then(function () {
          return reconcile(attempt + 1); // verify convergence, retry if not
        });
    });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      if (!res.ok) throw new Error('GET /cart.js failed: ' + res.status);
      return res.json();
    });
  }

  function writeCart(url, payload) {
    var headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    headers[SYNC_HEADER] = '1';

    return fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          // Shopify's Ajax API returns HTTP 200 with a `status`/
          // `description` error payload for some failures (e.g. sold
          // out), not always a non-2xx status — check both.
          if (!res.ok || data.status) {
            throw new Error(
              '[free-gift-sync] ' + url + ' rejected the write: ' +
              (data.description || data.message || res.status)
            );
          }
          return data;
        });
      })
      .then(function (data) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-sync' });
        }
        lockGiftQuantityInputs();
        return data;
      });
  }

  // ---- Hook 1: the theme's own cart-update PubSub event ----------
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      // Always re-lock the quantity inputs — the theme just replaced
      // the cart HTML wholesale, so any lock we applied is gone.
      lockGiftQuantityInputs();

      // Don't re-schedule a sync in response to our own refresh
      // broadcast — avoids pointless extra /cart.js round-trips.
      if (event && event.source === 'free-gift-sync') return;

      scheduleSync();
    });
  }

  // ---- Hook 2: wrap fetch to catch raw /cart/*.js calls that ------
  // ---- bypass the theme's PubSub system entirely (e.g. the -------
  // ---- product-page Add to Cart button in pf-pdp-hero.liquid, or -
  // ---- a direct add from a gift product's own product page). -----
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartMutation = /\/cart\/(add|change|update|clear)\.js/.test(url);
    var isOwnWrite = !!(init && init.headers && init.headers[SYNC_HEADER]);

    var promise = nativeFetch.apply(window, arguments);

    if (isCartMutation && !isOwnWrite) {
      promise.then(function () {
        scheduleSync();
      });
    }

    return promise;
  };

  // ---- Hook 3: MutationObserver catch-all -------------------------
  // Catches cart HTML being replaced by code that neither publishes
  // cart-update nor uses fetch for the write itself (or whose write
  // hook 2 already saw, but whose *re-render* still needs the lock
  // re-applied) — e.g. snippets/cart-boost.liquid's cbRefreshDrawer(),
  // which does a raw innerHTML swap on #CartDrawer.
  var cartMutationObserver = new MutationObserver(function (mutations) {
    var relevant = mutations.some(function (m) {
      return m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0);
    });
    if (!relevant) return;
    lockGiftQuantityInputs();
    scheduleSync();
  });

  function startObserving() {
    cartMutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  // ---- Quantity lock: disable each 'exact'-mode gift item's own ---
  // ---- stepper. 'floor'-mode rules are left unlocked — customers --
  // ---- may legitimately want to buy more than their free quota. --
  function lockGiftQuantityInputs() {
    GIFT_RULES.forEach(function (rule) {
      if (rule.mode === 'floor') return;
      document
        .querySelectorAll('input[data-quantity-variant-id="' + rule.id + '"]')
        .forEach(function (input) {
          if (input.dataset.freeGiftLocked === '1') return;
          input.dataset.freeGiftLocked = '1';
          input.disabled = true;
          input.setAttribute('aria-disabled', 'true');
          input.title = rule.label + ' quantity is managed automatically';

          var wrapper = input.closest('quantity-input');
          if (wrapper) {
            wrapper.querySelectorAll('.quantity__button').forEach(function (btn) {
              btn.disabled = true;
              btn.setAttribute('aria-disabled', 'true');
            });
          }
        });
    });
  }

  // ---- Hook 4: run once per page load -----------------------------
  function init() {
    lockGiftQuantityInputs();
    startObserving();
    scheduleSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
